import * as React from 'react';

import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

import {
  StripeCardElementOptions,
  StripeCardNumberElementChangeEvent,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
} from '@stripe/stripe-js';

import { Translate } from 'react-localize-redux';

import ButtonLoadingCtrl from '@webapp/controllers/button-loading';
import { StripePaymentData } from '@webapp/types/stripe/payment-data';
import styled, { useTheme } from 'styled-components';

interface StripeCardEditorProps {
  className?: string;
  onSuccess: (paymentMethod: StripePaymentData) => void;
  onStart: () => void;
  onError: (value: boolean) => void;
  errorMsg: string;
  customBtnLabel?: React.ReactNode;
  customBtnLabelLoading?: React.ReactNode;
  noBtn?: boolean;
  isMobile: boolean;
  isFullWidth?: boolean;
  noAbsoluteBtn?: boolean;
}

export const StripeCardEditor: React.FC<StripeCardEditorProps> = ({
  className,
  onSuccess,
  onStart,
  onError,
  errorMsg,
  customBtnLabel,
  customBtnLabelLoading,
  noBtn,
  isMobile,
  isFullWidth,
  noAbsoluteBtn,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [started, setStarted] = React.useState(false);
  const [complete, setComplete] = React.useState<string[]>([]);
  const [errors, setErrors] = React.useState<string[]>([]);
  const [zipCode, setZipCode] = React.useState('');
  const [fullName, setFullName] = React.useState('');

  const theme = useTheme();

  const isValid = !errors.length && fullName;

  const stripe = useStripe();
  const elements = useElements();

  React.useEffect(() => {
    if (errorMsg) {
      setLoading(false);
    }
  }, [errorMsg]);

  React.useEffect(() => {
    if (started) {
      onStart();
    }
  }, [started, onStart]);

  React.useEffect(() => {
    if (!zipCode || !fullName || errors.length) {
      onError(true);
    } else {
      onError(false);
    }
  }, [zipCode, fullName, errors, onError]);

  const buildCardOptions = (
    placeholder: string,
    extra: any = {},
    isMobile: boolean
  ): StripeCardElementOptions => ({
    style: {
      base: {
        fontSize: isMobile ? '16px' : '14px',
        lineHeight: '16px',
        fontWeight: '700',
        fontFamily: '"Inter", sans-serif',
        color: theme?.colors?.primary || '#000000',
        '::placeholder': {
          color: theme?.colors?.primary || '#000000',
          fontWeight: '700',
        },
      },
    },
    placeholder,
    ...extra,
  });

  const handleChange = (
    key: string,
    event:
      | StripeCardNumberElementChangeEvent
      | StripeCardCvcElementChangeEvent
      | StripeCardExpiryElementChangeEvent
  ) => {
    if (!started) {
      setStarted(true);
    }

    if (event.error || event.empty || !event.complete) {
      setErrors(errors.concat([key]));
      return;
    }

    setErrors(errors.filter((e) => e !== key));

    if (event.complete) {
      setComplete(complete.concat([key]));
    } else {
      setComplete(complete.filter((e) => e !== key));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements || !zipCode || !fullName || !isValid) {
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    if (!cardElement) {
      return;
    }

    setLoading(true);

    onSuccess({
      cardElement,
      fullName,
      zipCode,
    });

    return false;
  };

  return (
    <form id="stripe-editor-form" className={className} onSubmit={handleSubmit}>
      <CardNumberElement
        id="cardNumber"
        className="card-number"
        options={buildCardOptions('Card Number', { showIcon: true }, isMobile)}
        onChange={(event) => handleChange('cardNumber', event)}
      />
      <input
        id="cardName"
        name="fullName"
        type="text"
        placeholder="Cardholder's Name"
        className="StripeElement card-holder"
        value={fullName}
        onChange={(event) => setFullName(event.target.value)}
        required
      />
      <div className="container">
        <StyledInputContainer>
          <label>Expiry Date</label>
          <CardExpiryElement
            id="cardExpiry"
            className="card-expiry"
            options={buildCardOptions('MM/YY', { showIcon: false }, isMobile)}
            onChange={(event) => handleChange('cardExpiry', event)}
          />
        </StyledInputContainer>
        <StyledInputContainer>
          <label>CVV</label>
          <CardCvcElement
            id="cardCVV"
            className="card-cvv"
            options={buildCardOptions('...', { showIcon: false }, isMobile)}
            onChange={(event) => handleChange('cardCVV', event)}
          />
        </StyledInputContainer>
        <StyledInputContainer>
          <label>ZIP/Post</label>
          <input
            id="cardZip"
            name="zip"
            type="text"
            placeholder="....."
            className="StripeElement card-zip"
            value={zipCode}
            onChange={(event) => setZipCode(event.target.value)}
          />
        </StyledInputContainer>
      </div>
      <MoveDown isFullWidth={isFullWidth} noAbsoluteBtn={noAbsoluteBtn}>
        {!noBtn ? (
          <ButtonLoadingCtrl
            id="payBtn"
            className="mt-20"
            isLoading={loading}
            disabled={!isValid}
            fullWidth
            type="submit"
          >
            {loading ? (
              customBtnLabelLoading ? (
                customBtnLabelLoading
              ) : (
                <Translate id="PAYMENT.PROCESSING" />
              )
            ) : customBtnLabel ? (
              customBtnLabel
            ) : (
              <Translate id="PAYMENT.FINISH" />
            )}
          </ButtonLoadingCtrl>
        ) : null}
      </MoveDown>
    </form>
  );
};

const MoveDown = styled.div<{ isFullWidth?: boolean; noAbsoluteBtn?: boolean }>`
  position: ${({ noAbsoluteBtn }) => (noAbsoluteBtn ? 'static' : 'absolute')};
  padding-top: ${({ noAbsoluteBtn }) => (noAbsoluteBtn ? '0' : '176px')};
  text-align: center;
  width: ${({ isFullWidth }) => (isFullWidth ? '100%' : '27.5%')};
  min-width: 150px;
  cursor: pointer;
  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    padding-top: ${({ noAbsoluteBtn }) => (noAbsoluteBtn ? '0' : '190px')};
  }
`;

const StyledInputContainer = styled.span`
  position: relative;
  width: calc(100% / 3 - 3px);
  & > label {
    position: absolute;
    left: 12px;
    top: 13px;
    z-index: 1;
    font-size: 12px;
    line-height: 14px;
    color: ${({ theme }) => theme.colors.primary};

    ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
      left: 23px;
    }
  }
`;

export default styled(StripeCardEditor)`
  flex-grow: 1;
  width: 100%;
  position: relative;

  .container {
    display: flex;
    justify-content: space-between !important;
  }

  .card-number {
  }

  .card-expiry {
    min-width: 65px !important;
    padding-top: 30px !important;
    padding-bottom: 13px !important;
  }

  .card-cvv {
    min-width: fill-available;
    padding-top: 30px !important;
    padding-bottom: 13px !important;
  }

  .card-zip {
    min-width: 65px;
    padding-top: 30px !important;
    padding-bottom: 13px !important;
    max-height: 59px !important;
    ${({ theme }) => `@media screen and ${theme.device.laptopL}`} {
      min-width: fit-content;
    }
  }

  .card-number,
  .card-expiry,
  .card-cvv {
    & > div {
      height: 16px;
    }
  }

  .StripeElement {
    min-height: 48px;
    transition: border-left-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
    border-left: 3px solid;
    border-left-color: transparent;

    width: 100%;
    position: relative;
    border-radius: 4px;
    font-size: ${({ theme }) => theme.typography.h3};
    font-weight: 700;
    line-height: 16px;
    background-color: ${({ theme }) =>
      theme.colors.differentBackground} !important;
    color: ${({ theme }) => theme.colors.primary};
    cursor: auto;
    box-sizing: border-box;
    padding: 16px 12px 16px 9px;
    margin-right: 8px;
    margin-bottom: 5px !important;

    ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
      font-size: ${({ theme }) => theme.typography.h4};
      min-height: 60px;
      padding-left: 20px !important;
      padding-right: 20px !important;
    }

    &:last-child {
      margin-right: 0;
    }

    &::placeholder,
    &::-webkit-input-placeholder {
      color: ${({ theme }) => theme.colors.primary};
      font-weight: 700;
      opacity: 1;
    }
  }

  .StripeElement--invalid {
    border-left-color: ${({ theme }) => theme.colors.error};
  }
`;
